import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { preventEnterSubmit } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Copy, Check } from "lucide-react";
import { captureError } from "@/lib/sentry";

const workspaceSchema = z.object({
  name: z.string().min(1, "Company name is required"),
  timezone: z.string().min(1, "Timezone is required"),
});

export function WorkspaceSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const { currentWorkspace, refetch, defaultWorkspaceId, setDefaultWorkspace } = useWorkspaces();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [copiedWorkspaceId, setCopiedWorkspaceId] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const copyWorkspaceId = async () => {
    if (!currentWorkspace?.id) return;
    await navigator.clipboard.writeText(currentWorkspace.id);
    setCopiedWorkspaceId(true);
    setTimeout(() => setCopiedWorkspaceId(false), 2000);
  };

  const form = useForm<z.infer<typeof workspaceSchema>>({
    resolver: zodResolver(workspaceSchema),
    defaultValues: {
      name: "",
    },
  });

  // Reset form with current workspace name when it changes
  useEffect(() => {
    if (currentWorkspace) {
      form.reset({
        name: currentWorkspace.name,
        timezone: 'UTC',
      });
    }
  }, [currentWorkspace, form]);

  const onSubmit = async (data: z.infer<typeof workspaceSchema>) => {
    if (!currentWorkspace) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({ name: data.name })
        .eq("id", currentWorkspace.id);

      if (error) throw error;

      await refetch();

      toast({
        description: "Company name updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'workspace-name-update' });
      toast({
        title: "Error",
        description: "Failed to update company name",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const canDeleteWorkspace = currentWorkspace && (
    currentWorkspace.created_by === user?.id || 
    currentWorkspace.role === 'admin'
  );

  const handleDeleteWorkspace = async () => {
    if (!currentWorkspace || deleteConfirmation !== currentWorkspace.name) {
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", currentWorkspace.id);

      if (error) {
        console.error("Delete workspace error:", error);
        throw error;
      }

      toast({
        description: "Workspace deleted successfully",
      });

      // Clear the stored workspace ID before navigating
      localStorage.removeItem("currentWorkspaceId");
      navigate("/");
      window.location.reload(); // Refresh to update workspace context
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { message?: string })?.message || "Failed to delete workspace";
      console.error("Delete workspace error:", error);
      captureError(error as Error, { feature: 'workspace-delete' });
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  if (!currentWorkspace || !currentWorkspace.name) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading workspace...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">
          Company Details
        </h2>
        <p className="text-muted-foreground">
          Manage your company information
        </p>
      </div>

      <div className="space-y-8">
        {/* Company Icon */}
        <div className="space-y-4">
          <div className="flex items-center gap-8">
            <Avatar className="h-24 w-24">
              <AvatarImage src={""} />
              <AvatarFallback className="text-2xl">
                {currentWorkspace.name?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h3 className="font-medium">Upload company logo</h3>
              <p className="text-sm text-muted-foreground">
                JPG, GIF or PNG. Recommended size is 256×256px
              </p>
              <Button variant="outline" size="sm">
                Choose file
              </Button>
            </div>
          </div>
        </div>

        {/* Company Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company name</FormLabel>
                  <FormControl>
                    <Input {...field} className="max-w-md" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timezone</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="UTC">UTC (+00:00)</SelectItem>
                      <SelectItem value="America/New_York">New York (Eastern Time)</SelectItem>
                      <SelectItem value="America/Chicago">Chicago (Central Time)</SelectItem>
                      <SelectItem value="America/Denver">Denver (Mountain Time)</SelectItem>
                      <SelectItem value="America/Los_Angeles">Los Angeles (Pacific Time)</SelectItem>
                      <SelectItem value="America/Phoenix">Phoenix (Mountain Time - No DST)</SelectItem>
                      <SelectItem value="America/Anchorage">Anchorage (Alaska Time)</SelectItem>
                      <SelectItem value="Pacific/Honolulu">Honolulu (Hawaii Time)</SelectItem>
                      <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                      <SelectItem value="Europe/Paris">Paris (Central European Time)</SelectItem>
                      <SelectItem value="Europe/Berlin">Berlin (Central European Time)</SelectItem>
                      <SelectItem value="Asia/Dubai">Dubai (Gulf Time)</SelectItem>
                      <SelectItem value="Asia/Shanghai">Shanghai (China Time)</SelectItem>
                      <SelectItem value="Asia/Tokyo">Tokyo (Japan Time)</SelectItem>
                      <SelectItem value="Asia/Singapore">Singapore Time</SelectItem>
                      <SelectItem value="Australia/Sydney">Sydney (Eastern Australia Time)</SelectItem>
                      <SelectItem value="Pacific/Auckland">Auckland (New Zealand Time)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </form>
        </Form>

        {/* Default workspace */}
        <section className="mt-6 border-t pt-6">
          <h3 className="text-sm font-semibold mb-1">Default workspace</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Your default workspace auto-loads when you sign in. Within a session,
            switching workspaces persists until you sign out.
          </p>

          {currentWorkspace && (() => {
            const isDefault = currentWorkspace.id === defaultWorkspaceId;
            return (
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={isDefault}
                  disabled={isDefault}
                  onCheckedChange={() => {
                    if (!isDefault) setDefaultWorkspace(currentWorkspace.id);
                  }}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  {isDefault
                    ? "This is currently your default workspace."
                    : "Make this my default workspace"}
                </span>
              </label>
            );
          })()}
        </section>

        {/* Usage Stats */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">
            You are currently on a {currentWorkspace.plan_id || "Free"} plan
          </h3>

          <div className="grid grid-cols-3 gap-4">
            <UsageCard
              title="Custom domain messages"
              used={0}
              total={5000}
            />
            <UsageCard
              title="Sender domains"
              used={0}
              total={5000}
            />
            <UsageCard
              title="Contacts"
              used={currentWorkspace.contact_count || 0}
              total={5000}
            />
          </div>
        </div>

        {/* Workspace ID - for API reference */}
        <div className="pt-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground/60">Workspace ID:</span>
            <code className="text-xs text-muted-foreground/60 font-mono">{currentWorkspace.id}</code>
            <button
              onClick={copyWorkspaceId}
              className="p-1 hover:bg-accent rounded transition-colors"
              title="Copy Workspace ID"
            >
              {copiedWorkspaceId ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground/60" />
              )}
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="space-y-4 border-t pt-6">
          <h3 className="text-lg font-semibold text-destructive">
            Data deletion
          </h3>
          {canDeleteWorkspace ? (
            <>
              <p className="text-sm text-muted-foreground">
                If you would like to permanently delete this workspace and all of its data, click on "Delete workspace"
              </p>
              <Button 
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete workspace
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Only workspace admins can delete this workspace
            </p>
          )}
        </div>

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Workspace</DialogTitle>
              <DialogDescription>
                This action cannot be undone. This will delete the
                workspace and all associated data.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Please type <span className="font-semibold">{currentWorkspace.name}</span> to confirm.
              </p>
              <Input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Enter workspace name"
                className="max-w-md"
              />
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteWorkspace}
                disabled={deleteConfirmation !== currentWorkspace.name || isLoading}
              >
                {isLoading ? "Deleting..." : "Delete Workspace"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

interface UsageCardProps {
  title: string;
  used: number;
  total: number;
}

function UsageCard({ title, used, total }: UsageCardProps) {
  const percentage = (used / total) * 100;

  return (
    <div className="p-4 border rounded-lg space-y-4">
      <h4 className="font-medium">{title}</h4>
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground">
          <span>Used {used}/{total}</span>
        </div>
        <Progress value={percentage} className="h-2" />
        <div className="text-sm text-muted-foreground">
          <span>Remaining {total - used}</span>
        </div>
      </div>
    </div>
  );
}